import React, { useEffect, useState } from "react";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Star, Share2, AlertTriangle, Hourglass } from "lucide-react";
import { fmtD } from "../lib/date";

export default function OwnerSatisfactionBoard({ zh }) {
  const { api } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/crm/owner-satisfaction").then(({ data: d }) => setData(d)).catch(() => {});
  }, []); // eslint-disable-line

  if (!data) return null;
  const stars = (n, sz = "h-3.5 w-3.5") => [1, 2, 3, 4, 5].map((i) => (
    <Star key={i} className={`${sz} ${i <= (n || 0) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
  ));
  const shareCard = (r) =>
    window.open(`${api.defaults.baseURL}/crm/owner-surveys/${r.id}/share-card?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`, "_blank");
  const maxDist = Math.max(1, ...Object.values(data.dist || {}));

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="owner-sat-board">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-white flex-wrap">
        <Star className="h-4 w-4 text-amber-500 fill-amber-400 shrink-0" />
        <h3 className="text-sm font-bold text-slate-800">{zh ? "业主满意度" : "Owner Satisfaction"}</h3>
        {data.avg != null && (
          <span className="inline-flex items-center gap-1 text-sm font-black text-amber-600" data-testid="owner-sat-avg">
            {data.avg}<span className="text-[10px] font-semibold text-slate-400">/ 5 · {data.count} {zh ? "份" : "reviews"}</span>
          </span>
        )}
        {data.low > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 bg-red-100 rounded-full px-2 py-0.5" data-testid="owner-sat-low-count">
            <AlertTriangle className="h-3 w-3" />{zh ? `差评 ${data.low}` : `${data.low} low`}
          </span>
        )}
        {data.pendingCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5" data-testid="owner-sat-pending">
            <Hourglass className="h-3 w-3" />{zh ? `待回收 ${data.pendingCount}` : `${data.pendingCount} pending`}
          </span>
        )}
      </div>
      {data.count === 0 ? (
        <p className="px-4 py-6 text-xs text-slate-400 text-center" data-testid="owner-sat-empty">{zh ? "暂无业主评价数据（项目完工后自动发送评分邀请）" : "No reviews yet — surveys are sent automatically on project completion."}</p>
      ) : (
        <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-1.5" data-testid="owner-sat-dist">
            {[5, 4, 3, 2, 1].map((n) => (
              <div key={n} className="flex items-center gap-2 text-[11px]">
                <span className="w-6 text-right font-semibold text-slate-500">{n}★</span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${n >= 4 ? "bg-amber-400" : n === 3 ? "bg-orange-400" : "bg-red-500"}`}
                    style={{ width: `${((data.dist?.[String(n)] || 0) / maxDist) * 100}%` }} />
                </div>
                <span className="w-5 text-slate-400">{data.dist?.[String(n)] || 0}</span>
              </div>
            ))}
          </div>
          <div className="lg:col-span-2 space-y-2">
            {data.recent.map((r) => (
              <div key={r.id} data-testid={`owner-sat-row-${r.id}`}
                className={`rounded-lg border px-3 py-2 ${r.rating <= 3 ? "border-red-200 bg-red-50" : "border-slate-100 bg-slate-50"}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex">{stars(r.rating)}</span>
                  <span className="text-xs font-semibold text-slate-700 truncate max-w-[200px]">{r.clientName}</span>
                  <span className="text-[10px] text-slate-400 truncate max-w-[220px]">{r.address}</span>
                  {r.rating <= 3 && <span data-testid={`owner-sat-lowbadge-${r.id}`} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">{zh ? "差评·已派回访" : "Low · follow-up"}</span>}
                  <span className="flex-1" />
                  <span className="text-[10px] text-slate-300">{fmtD(r.submittedAt)}</span>
                  {r.rating === 5 && (
                    <button data-testid={`owner-sat-share-${r.id}`} onClick={() => shareCard(r)}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 border border-amber-300 hover:bg-amber-100 rounded px-1.5 py-0.5">
                      <Share2 className="h-3 w-3" />{zh ? "分享卡片" : "Share card"}
                    </button>
                  )}
                </div>
                {r.comment && <p className="mt-1 text-[11px] text-slate-500 line-clamp-2">“{r.comment}”</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
